// Accueil : trois actions, puis ce qui demande réellement une action. Aucun chiffre décoratif.
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Building2, CalendarCheck, ChevronRight, FlaskConical, Info, XCircle } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { depuis } from '../../lib/format.ts';
import { Alerte, Badge, Chargement, EtatErreur, EtatVide, LienBouton, Panneau } from '../../components/ui.tsx';
import { texteEtatEngagement } from '../admin/OngletSalaries.tsx';
import { BadgeStatutIntervention, BadgeStatutDossier } from '../../components/statuts.tsx';
import { BoutonChargerDemo } from '../../components/BoutonChargerDemo.tsx';
import type { MaJournee } from '../interventions/chantiers/types.ts';

interface Accueil {
  nbSites: number; nbSitesDemo: number;
  interventions: { id: string; reference: string; title: string; status: string; updated_at: string; site_name: string; equipement: string | null; demo: boolean }[];
  dossiers: { id: string; reference: string; title: string; status: string; updated_at: string; site_name: string; a_traiter: number; questions: number; demo: boolean }[];
  alertes: { type: string; texte: string; to: string; ton: 'ambre' | 'danger' | 'info' }[];
}

/** Pictogrammes tracés : un fil, un nœud, un geste métier. */
const Picto = {
  diagnostic: (
    <svg viewBox="0 0 64 40" aria-hidden><path d="M2 20h14l5-12 7 24 6-16 4 4h24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx="58" cy="20" r="4" fill="var(--accent)" /></svg>
  ),
  analyse: (
    <svg viewBox="0 0 64 40" aria-hidden><rect x="4" y="4" width="22" height="30" rx="2" fill="none" stroke="currentColor" strokeWidth="2.2" />
      <path d="M9 12h12M9 18h12M9 24h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M26 18h12" stroke="var(--accent)" strokeWidth="2.4" strokeDasharray="3 3" />
      <rect x="38" y="6" width="22" height="30" rx="2" fill="none" stroke="currentColor" strokeWidth="2.2" />
      <path d="M43 14h12M43 20h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><circle cx="54" cy="27" r="3.5" fill="var(--accent)" /></svg>
  ),
  conception: (
    <svg viewBox="0 0 64 40" aria-hidden><path d="M10 30V10h22M32 10v20h22M32 20H10" fill="none" stroke="currentColor" strokeWidth="2.2" />
      {[[10, 10], [10, 30], [32, 10], [32, 30], [54, 30], [10, 20]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r="3.6" fill={i === 4 ? 'var(--accent)' : 'var(--surface)'} stroke="currentColor" strokeWidth="2" />)}</svg>
  ),
};

function Action({ to, titre, texte, picto, detail }: { to: string; titre: string; texte: string; picto: ReactElement; detail?: string }) {
  return (
    <Link to={to} className="action-accueil">
      <span className="action-accueil-picto">{picto}</span>
      <span className="pile" style={{ gap: 4 }}>
        <span className="action-accueil-titre">{titre}</span>
        <span className="petit secondaire">{texte}</span>
        {detail && <span className="tres-petit discret">{detail}</span>}
      </span>
      <ChevronRight aria-hidden className="action-accueil-fleche" />
    </Link>
  );
}

/** Carte « Ma journée » : en tête de l'Accueil, seulement si le compte connecté est affecté aujourd'hui (conception § 1.1). */
function CarteMaJournee() {
  const q = useQuery({ queryKey: ['ma-journee'], queryFn: () => api.get<MaJournee>('/api/ma-journee') });
  const l = q.data?.jours[0]?.jour === q.data?.aujourdhui ? q.data?.jours[0]?.affectations ?? [] : [];
  if (!l.length) return null;
  const a = l[0];
  return (
    <Link to="/interventions/journee" className="action-accueil mj-accueil">
      <span className="mj-accueil-icone"><CalendarCheck aria-hidden /></span>
      <span className="pile" style={{ gap: 4, minWidth: 0 }}>
        <span className="action-accueil-titre">Ma journée{a.chantier.demo && <> <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge></>}</span>
        <span className="secondaire mj-accueil-titre">{a.chantier.reference && <span className="mono">{a.chantier.reference} </span>}{a.chantier.title}</span>
        <span className="petit discret mj-accueil-detail">{a.poste ? a.poste.label : a.site.nom}{l.length > 1 ? ` — et ${l.length - 1} autre${l.length > 2 ? 's' : ''} aujourd’hui` : ''}</span>
      </span>
      <ChevronRight aria-hidden className="action-accueil-fleche" />
    </Link>
  );
}

/**
 * Données des salariés (RGPD) : pour l'administrateur seulement, tant que le représentant légal n'a pas confirmé les réglages en
 * vigueur (aucun engagement, engagement fictif de démonstration, ou réglages modifiés depuis). Rien n'est bloqué.
 */
function BandeauEngagementSalaries() {
  const { moi } = useSession();
  const e = moi.organisation.salaries?.engagement;
  if (!e || e.etat === 'a_jour') return null;
  const t = texteEtatEngagement(e.etat, e.le);
  return (
    <Alerte ton={t.ton} titre={t.titre}>
      <span>{t.texte}</span>
      <span className="groupe-btn" style={{ marginTop: 'var(--e-2)' }}><LienBouton compact to="/administration?onglet=salaries">Données des salariés (RGPD)</LienBouton></span>
    </Alerte>
  );
}

export function PageAccueil() {
  const { moi, peut } = useSession();
  const q = useQuery({ queryKey: ['accueil'], queryFn: () => api.get<Accueil>('/api/accueil') });
  const prenom = moi.utilisateur.nom.split(' ')[0];

  if (q.isLoading) return <Chargement lignes={5} />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const a = q.data!;
  const enCours = a.interventions.filter((i) => ['brouillon', 'en_cours'].includes(i.status)).length;

  return (
    <div className="pile-l">
      <header className="pile" style={{ gap: 4 }}>
        <h1>Bonjour {prenom}</h1>
        <p className="secondaire">Que faut-il traiter aujourd’hui ?</p>
      </header>

      <BandeauEngagementSalaries />
      {peut('chantier.lire') && <CarteMaJournee />}

      <div className="actions-accueil">
        <Action to="/interventions/nouvelle" titre="Diagnostiquer une panne" picto={Picto.diagnostic}
          texte="Identifier l’équipement, décrire le symptôme, suivre des contrôles sourcés."
          detail={enCours ? `${enCours} intervention${enCours > 1 ? 's' : ''} en cours` : undefined} />
        <Action to="/etudes/nouveau" titre="Analyser un dossier" picto={Picto.analyse}
          texte="Comparer la demande, le CCTP et le chiffrage pour repérer oublis et ambiguïtés." />
        <Action to="/etudes?pour=conception" titre="Concevoir et chiffrer" picto={Picto.conception}
          texte="Architecture du système, cheminements, nomenclature et devis." />
      </div>

      {a.nbSites === 0 && (
        <Panneau>
          <EtatVide icone={<Building2 />} titre="Aucun site pour le moment"
            texte="Commencez par créer le site d’un client, ou ouvrez l’exemple de démonstration : un remplacement de tableaux électriques dont le CCTP ajoute comptages, supervision et programmation. Ses données sont fictives et marquées comme telles."
            action={<div className="groupe-btn">{peut('site.ecrire') && <LienBouton to="/sites/nouveau" variante="principal" icone={<Building2 aria-hidden />}>Créer un site</LienBouton>}
              <BoutonChargerDemo />
            </div>} />
        </Panneau>
      )}

      {a.alertes.length > 0 && (
        <section aria-labelledby="t-alertes" className="pile">
          <h2 id="t-alertes" style={{ fontSize: 'var(--t-l)' }}>À traiter</h2>
          <div className="panneau liste">
            {a.alertes.map((al) => (
              <Link key={al.type} to={al.to} className="liste-element">
                {al.ton === 'danger' ? <XCircle aria-hidden style={{ color: 'var(--danger)' }} /> : al.ton === 'ambre'
                  ? <AlertTriangle aria-hidden style={{ color: 'var(--ambre)' }} /> : <Info aria-hidden style={{ color: 'var(--info)' }} />}
                <span className="liste-element-corps"><span>{al.texte}</span></span>
                <ChevronRight aria-hidden size={18} />
              </Link>
            ))}
          </div>
        </section>
      )}

      {a.nbSites > 0 && (
        <div className="grille-2">
          <Panneau titre="Interventions récentes" serre actions={<LienBouton to="/interventions" compact variante="fantome">Toutes</LienBouton>}>
            {a.interventions.length === 0
              ? <EtatVide titre="Aucune intervention" texte="Les diagnostics que vous lancez apparaissent ici." />
              : <div className="liste">{a.interventions.map((i) => (
                <Link key={i.id} to={`/interventions/${i.id}`} className="liste-element">
                  <span className="liste-element-corps">
                    <span className="liste-element-titre">{i.title}</span>
                    <span className="petit discret"><span className="mono">{i.reference}</span> — {i.site_name}{i.equipement ? `, ${i.equipement}` : ''} — {depuis(i.updated_at)}</span>
                  </span>
                  {i.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}
                  <BadgeStatutIntervention statut={i.status} />
                </Link>))}</div>}
          </Panneau>
          <Panneau titre="Dossiers à valider" serre actions={<LienBouton to="/etudes" compact variante="fantome">Tous</LienBouton>}>
            {a.dossiers.length === 0
              ? <EtatVide titre="Aucun dossier ouvert" texte="Importez les pièces d’un appel d’offres pour en extraire les exigences." />
              : <div className="liste">{a.dossiers.map((x) => (
                <Link key={x.id} to={`/etudes/${x.id}`} className="liste-element">
                  <span className="liste-element-corps">
                    <span className="liste-element-titre">{x.title}</span>
                    <span className="petit discret"><span className="mono">{x.reference}</span> — {x.site_name}
                      {x.a_traiter ? ` — ${x.a_traiter} exigence${x.a_traiter > 1 ? 's' : ''} à traiter` : ''}
                      {x.questions ? ` — ${x.questions} question${x.questions > 1 ? 's' : ''} à poser` : ''}</span>
                  </span>
                  {x.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}
                  <BadgeStatutDossier statut={x.status} />
                </Link>))}</div>}
          </Panneau>
        </div>
      )}
    </div>
  );
}
